import React, { useEffect, useState } from "react";
import logo from "../../../logo.png"; // Replace with your logo image path

const loadingMessages = [
  "Initializing dashboard...",
  "Loading analytics data...",
  "Preparing charts and graphs...",
  "Connecting to data sources...",
  "Almost ready...",
  "Finalizing components...",
];

const mockComponents = [
  "Sales Dashboard",
  "Analytics Charts",
  "User Metrics",
  "Performance Data",
  "Revenue Tracking",
  "Client Statistics",
  "Marketing Insights",
  "Growth Analytics",
];

const Loader = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [activeComponents, setActiveComponents] = useState([]);
  const [spinnerRotation, setSpinnerRotation] = useState(0);

  useEffect(() => {
    // Simulate realistic loading progress
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        // Simulate realistic loading curve - faster at start, slower at end
        const increment =
          prev < 50 ? Math.random() * 8 + 2 : Math.random() * 3 + 1;
        return Math.min(100, prev + increment);
      });
    }, 200);

    return () => clearInterval(progressInterval);
  }, []);

  useEffect(() => {
    // Change loading message based on progress
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => {
        const progressBasedIndex = Math.floor(
          (progress / 100) * (loadingMessages.length - 1)
        );
        return Math.min(progressBasedIndex, loadingMessages.length - 1);
      });
    }, 1000);

    return () => clearInterval(messageInterval);
  }, [progress]);

  useEffect(() => {
    // Animate components appearing/disappearing
    const componentInterval = setInterval(() => {
      setActiveComponents((prev) => {
        const newComponents = [...prev];
        const randomComponent =
          mockComponents[Math.floor(Math.random() * mockComponents.length)];

        if (newComponents.includes(randomComponent)) {
          // Remove component
          return newComponents.filter((comp) => comp !== randomComponent);
        } else {
          // Add component (max 3 visible at once)
          if (newComponents.length < 3) {
            return [...newComponents, randomComponent];
          } else {
            // Replace oldest component
            return [randomComponent, ...newComponents.slice(0, 2)];
          }
        }
      });
    }, 800);

    return () => clearInterval(componentInterval);
  }, []);

  useEffect(() => {
    // Spinning animation for the small loader
    const spinInterval = setInterval(() => {
      setSpinnerRotation((prev) => (prev + 45) % 360);
    }, 100);

    return () => clearInterval(spinInterval);
  }, []);

  const getProgressColor = () => {
    if (progress < 30) return "#ff6b6b";
    if (progress < 70) return "#ffd93d";
    return "#6bcf7f";
  };

  return (
    <div className="loading-screen">
      {/* Progress bar at top */}
      <div className="progress-bar-container">
        <div
          className="progress-bar"
          style={{
            width: `${progress}%`,
            backgroundColor: getProgressColor(),
            transition: "width 0.3s ease, background-color 0.5s ease",
          }}
        />
      </div>

      {/* Main content */}
      <div className="loading-content">
        {/* Logo */}
        <img src={logo} alt="Logo" className="loading-logo" />

        {/* Small spinning loader */}
        <div className="spinner-container">
          <div
            className="small-spinner"
            style={{
              transform: `rotate(${spinnerRotation}deg)`,
              transition: "transform 0.1s linear",
            }}
          />
        </div>

        {/* Loading message */}
        <div className="loading-message">{loadingMessages[messageIndex]}</div>

        {/* Progress percentage */}
        <div className="progress-text">{Math.round(progress)}%</div>
      </div>

      {/* Flashing components */}
      <div className="component-simulation">
        {activeComponents.map((component, index) => (
          <div
            key={`${component}-${index}`}
            className="mock-component"
            style={{
              animationDelay: `${index * 0.2}s`,
              left: `${20 + index * 25}%`,
              top: `${60 + index * 10}%`,
            }}
          >
            <div className="component-icon" />
            <span className="component-label">{component}</span>
          </div>
        ))}
      </div>

      {/* Background elements for depth */}
      <div className="background-elements">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="bg-element"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default Loader;
